/**
 * WalkthroughRunnerPage — Landlord Portal (JR-2 / JR-4 / JR-6)
 *
 * Mobile-first walkthrough capture: room list -> item list -> large "OK" tap, or
 * "Flag" into a deficiency detail (multi-select deficiency types, wide/closeup camera
 * capture via presigned upload, narrative). Shows a completeness indicator, resumes at
 * the server-tracked cursor (`summary.cursorItemId`), and offers a pre-submit review
 * that surfaces any submit-blocking gaps. While the walkthrough is a DRAFT, every
 * already-assessed item carries a persistent Edit affordance (BR-24/US-18) to re-open
 * it, flip OK<->Flag, edit the narrative, and add or remove photos — including from the
 * pre-submit review itself. A photoless OK item additionally carries a tile-level,
 * non-blocking photo nudge (BR-6) alongside Edit — a one-tap shortcut into detail that
 * disappears once a photo is attached. Once the walkthrough is frozen (SUBMITTED /
 * ACKNOWLEDGED — BR-7 single-write-guard), items become read-only and a correction-note
 * affordance (append-only, JR-6) replaces the mark/edit/nudge controls.
 *
 * Route: mounted at a lease-scoped path with `leaseId` and `phase` params (e.g.
 * `/leases/:leaseId/condition-walkthroughs/:phase`, `phase` = `move-in` | `move-out`).
 * Always operates as `actorRole=LANDLORD` — a landlord never holds a magic-link token.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Camera, Check, Flag, ChevronLeft, AlertTriangle, MessageSquarePlus, Pencil, X, ImageIcon, FileText, Loader2 } from 'lucide-react';
import {
  getWalkthroughForLease,
  createWalkthrough,
  getWalkthrough,
  markConditionItem,
  submitWalkthrough,
  addConditionCorrection,
  uploadConditionPhoto,
  deleteConditionPhoto,
  conditionPhotoDownloadUrl,
  getLease,
  DEFICIENCY_TYPE_LABELS,
  ITEM_CATEGORY_LABELS,
  WalkthroughIncompleteClientError,
  UnitTemplateEmptyClientError,
  type WalkthroughPhase,
  type WalkthroughSummaryResponse,
  type ConditionItemResponse,
  type ConditionPhotoResponse,
  type DeficiencyType,
  type SubmitGap,
  type PhotoKind,
  type PhotoStatus,
} from '../lib/api';
import type { LeaseDetail } from '../types/lease';
import { useConditionPhotoApprovals } from '../hooks/useCentrifugo';
import { PageHeader } from '../components/layout/index';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { Modal } from '../components/Modal';
import { groupItemsByRoom } from '../lib/condition-rooms';

const DEFICIENCY_TYPES = Object.keys(DEFICIENCY_TYPE_LABELS) as DeficiencyType[];

type View = 'rooms' | 'items' | 'detail' | 'review';

function normalizePhase(raw: string | undefined): WalkthroughPhase | null {
  if (!raw) return null;
  const upper = raw.toUpperCase().replace(/-/g, '_');
  return upper === 'MOVE_IN' || upper === 'MOVE_OUT' ? (upper as WalkthroughPhase) : null;
}

export function WalkthroughRunnerPage() {
  const params = useParams<{ leaseId: string; phase: string }>();
  const leaseId = params.leaseId;
  const phase = normalizePhase(params.phase);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<WalkthroughSummaryResponse | null>(null);
  const [items, setItems] = useState<ConditionItemResponse[]>([]);
  // Set only when the unit has no condition template yet (UNIT_TEMPLATE_EMPTY) — lets the
  // "no template" notice link to the unit's Condition tab even though no walkthroughId
  // (and thus no summary.unitId) exists yet.
  const [templateMissingUnitId, setTemplateMissingUnitId] = useState<string | null>(null);

  const [view, setView] = useState<View>('rooms');
  const [selectedRoom, setSelectedRoom] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [hasResumed, setHasResumed] = useState(false);

  // Deficiency/assessment detail form state
  const [deficiencyTypes, setDeficiencyTypes] = useState<DeficiencyType[]>([]);
  const [narrative, setNarrative] = useState('');
  // Which condition the detail view is being used to save (BR-6): 'DEFICIENT' when opened
  // via "Flag"; 'OK' when opened via the optional "add photos" nudge on an item already
  // marked OK. OK items never require a photo in either capture mode — the nudge is a
  // non-blocking suggestion only (see the completeness gate in
  // condition-walkthrough.service.ts#submit).
  const [detailTarget, setDetailTarget] = useState<'OK' | 'DEFICIENT'>('DEFICIENT');
  const [isSavingMark, setIsSavingMark] = useState(false);
  const [markError, setMarkError] = useState<string | null>(null);
  const [sessionPhotos, setSessionPhotos] = useState<Record<string, ConditionPhotoResponse[]>>({});
  const [uploadingKind, setUploadingKind] = useState<PhotoKind | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [removingPhotoId, setRemovingPhotoId] = useState<string | null>(null);
  // FM5: photo ids whose <img> failed to load. For a still-PENDING photo this is the normal
  // mid-normalization state (the raw upload — e.g. an iPhone HEIC — isn't browser-renderable
  // until the out-of-repo normalizer produces a web-friendly copy), rendered as a neutral
  // "Processing…" placeholder; for an already-APPROVED photo it's a genuine "Unavailable"
  // error. Reset whenever a different item's detail view opens.
  const [failedPhotoIds, setFailedPhotoIds] = useState<Set<string>>(new Set());
  // A ref mirror so the Centrifugo approval handler can tell whether a just-approved photo was
  // being shown as failed (and thus needs a cache-busted reload) without re-subscribing.
  const failedPhotoIdsRef = useRef<Set<string>>(failedPhotoIds);
  failedPhotoIdsRef.current = failedPhotoIds;
  // Photo ids approved live via Centrifugo after their first <img> load failed: their download
  // URL gets a cache-busting suffix so the browser re-fetches the now-normalized bytes rather
  // than serving the cached, un-decodable raw upload.
  const [refreshedPhotoIds, setRefreshedPhotoIds] = useState<Set<string>>(new Set());
  // FM6/FM7: the photo awaiting the themed remove-confirmation modal (replaces
  // window.confirm, which is un-themed and breaks in dark mode).
  const [photoPendingRemoval, setPhotoPendingRemoval] = useState<{ id: string } | null>(null);
  const generalInputRef = useRef<HTMLInputElement>(null);

  // Correction form state (frozen items — JR-6)
  const [correctionItemId, setCorrectionItemId] = useState<string | null>(null);
  const [correctionNote, setCorrectionNote] = useState('');
  const [isSavingCorrection, setIsSavingCorrection] = useState(false);
  const [correctionError, setCorrectionError] = useState<string | null>(null);

  // Submit / review state
  const [submitGaps, setSubmitGaps] = useState<SubmitGap[] | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // BR-27: landlord-only override to submit with an all-MISSING_PHOTO gap set. Reset to
  // false at the top of every handleSubmitWalkthrough call so a stale checked box can
  // never silently override a later, different gap set.
  const [confirmPhotoOverride, setConfirmPhotoOverride] = useState(false);

  const isFrozen = summary ? summary.status !== 'DRAFT' : false;

  const loadWalkthrough = useCallback(async () => {
    if (!leaseId || !phase) return;
    setIsLoading(true);
    setError(null);
    setTemplateMissingUnitId(null);
    try {
      const existing = await getWalkthroughForLease(leaseId, phase, 'LANDLORD');
      const walkthroughId = existing.walkthrough
        ? existing.walkthrough.id
        : (await createWalkthrough(leaseId, phase)).walkthrough.id;
      const detail = await getWalkthrough(walkthroughId);
      setSummary(detail.summary);
      setItems(detail.items);
    } catch (err) {
      if (err instanceof UnitTemplateEmptyClientError && leaseId) {
        setItems([]);
        setSummary(null);
        try {
          // getLease's declared LeaseResponse type ({ lease: LeaseDetail }) doesn't match
          // this endpoint's actual flat response shape — same pre-existing mismatch
          // LeaseDetailPage.tsx works around (see its loadData: `as Promise<unknown>` +
          // cast to LeaseDetail, then reads `.unitId` directly, not `.unit.id`).
          const leaseData = (await getLease('', leaseId)) as unknown as LeaseDetail;
          setTemplateMissingUnitId(leaseData.unitId);
        } catch {
          // No unitId available for the "Set up template" link — the notice still renders.
        }
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load walkthrough');
      }
    } finally {
      setIsLoading(false);
    }
  }, [leaseId, phase]);

  useEffect(() => {
    loadWalkthrough();
  }, [loadWalkthrough]);

  // Resume at the server-tracked cursor on first load of a DRAFT walkthrough (JR-2/JR-4).
  // Lands on the cursor item's room list (rather than jumping straight into the detail
  // view) since — post-fix — the detail view requires knowing whether the resumed item
  // is being marked OK or Flagged, which the cursor alone doesn't tell us.
  useEffect(() => {
    if (hasResumed || !summary || items.length === 0) return;
    setHasResumed(true);
    if (summary.status === 'DRAFT' && summary.cursorItemId) {
      const cursorItem = items.find((i) => i.id === summary.cursorItemId);
      if (cursorItem) {
        setSelectedRoom(cursorItem.roomName);
        setView('items');
      }
    }
  }, [hasResumed, summary, items]);

  const roomGroups = useMemo(() => groupItemsByRoom(items), [items]);

  const assessedCount = items.filter((i) => i.condition !== 'NOT_ASSESSED').length;
  const deficientCount = items.filter((i) => i.condition === 'DEFICIENT').length;

  /**
   * Opens the item detail view. `target` drives both what the detail view asks for and
   * what `saveAssessment` submits: 'DEFICIENT' (from "Flag" or the persistent Edit
   * affordance on a flagged item) requires >=1 deficiency type; 'OK' (from the persistent
   * Edit affordance, or the tile-level photo nudge, on an item already marked OK)
   * requires no deficiency type and, per BR-6, no photo either — a photo is always
   * optional on an OK item in either capture mode.
   */
  function openItemDetail(item: ConditionItemResponse, target: 'OK' | 'DEFICIENT' = 'DEFICIENT') {
    setSelectedItemId(item.id);
    setDetailTarget(target);
    setDeficiencyTypes(target === 'DEFICIENT' ? item.deficiencyTypes : []);
    setNarrative(item.narrative ?? '');
    setMarkError(null);
    setUploadError(null);
    setFailedPhotoIds(new Set());
    setView('detail');
  }

  const selectedItem = items.find((i) => i.id === selectedItemId) ?? null;

  /**
   * BR-24: existing (server-recorded) photos UNIONED with this-session uploads, so a
   * resumed or re-opened item shows what's already on record, not just what was just
   * captured. De-duplicated by id — defensive only, since `items[].photos` isn't itself
   * re-synced on upload (only `photoCount` is), so no overlap occurs today. Carries
   * `status` for informational purposes only — it is NOT a viewability signal (a
   * `PENDING` photo is real and raw-viewable through the download proxy; only the
   * out-of-repo classifier worker flips it to `APPROVED`, which can take a while or
   * never run at all in dev). Viewability is determined solely by whether the `<img>`
   * actually fails to load (`onError` → `failedPhotoIds`, see FM5).
   */
  const displayedPhotos: Array<{ id: string; kind: PhotoKind; status: PhotoStatus }> = useMemo(() => {
    if (!selectedItem) return [];
    const fresh = sessionPhotos[selectedItem.id] ?? [];
    const freshIds = new Set(fresh.map((p) => p.id));
    const existing = (selectedItem.photos ?? [])
      .filter((p) => !freshIds.has(p.id))
      .map((p) => ({ id: p.id, kind: p.kind, status: p.status }));
    return [...existing, ...fresh.map((p) => ({ id: p.id, kind: p.kind, status: p.status }))];
  }, [selectedItem, sessionPhotos]);

  /**
   * Live approval from the out-of-repo normalizer (Centrifugo, condition-photos:<walkthroughId>).
   * When a photo we're showing as failed (its raw upload wasn't browser-renderable yet) flips to
   * APPROVED, drop it from `failedPhotoIds` and cache-bust its URL so the <img> re-fetches the
   * normalized bytes — swapping "Processing…" for the real thumbnail without a page reload. Also
   * reflect APPROVED in local state so any later load failure reads as a real error, not
   * "processing". Photos already rendering fine (e.g. a raw JPEG) are left untouched (no flicker).
   */
  const handlePhotoApproved = useCallback((photoId: string) => {
    if (failedPhotoIdsRef.current.has(photoId)) {
      setFailedPhotoIds((prev) => {
        if (!prev.has(photoId)) return prev;
        const next = new Set(prev);
        next.delete(photoId);
        return next;
      });
      setRefreshedPhotoIds((prev) => (prev.has(photoId) ? prev : new Set(prev).add(photoId)));
    }
    setSessionPhotos((prev) => {
      let changed = false;
      const next: Record<string, ConditionPhotoResponse[]> = {};
      for (const [itemId, photos] of Object.entries(prev)) {
        next[itemId] = photos.map((p) => {
          if (p.id === photoId && p.status !== 'APPROVED') {
            changed = true;
            return { ...p, status: 'APPROVED' as PhotoStatus };
          }
          return p;
        });
      }
      return changed ? next : prev;
    });
    setItems((prev) =>
      prev.map((item) =>
        item.photos?.some((p) => p.id === photoId)
          ? { ...item, photos: item.photos.map((p) => (p.id === photoId ? { ...p, status: 'APPROVED' as PhotoStatus } : p)) }
          : item
      )
    );
  }, []);

  useConditionPhotoApprovals(summary?.id ?? null, handlePhotoApproved);

  /** Instant one-tap OK (BR-6) — valid in both DEFICIENCY and HOLISTIC mode; OK items never require a photo. */
  const markOk = async (item: ConditionItemResponse) => {
    setMarkError(null);
    try {
      const { item: updated } = await markConditionItem(summary!.id, item.id, { condition: 'OK' });
      setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark item OK');
    }
  };

  const toggleDeficiencyType = (type: DeficiencyType) => {
    setDeficiencyTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
  };

  const saveAssessment = async () => {
    if (!selectedItem || !summary) return;
    if (detailTarget === 'DEFICIENT' && deficiencyTypes.length === 0) {
      setMarkError('Select at least one deficiency type.');
      return;
    }
    setIsSavingMark(true);
    setMarkError(null);
    try {
      const { item: updated } = await markConditionItem(summary.id, selectedItem.id, {
        condition: detailTarget,
        deficiencyTypes: detailTarget === 'DEFICIENT' ? deficiencyTypes : [],
        narrative: narrative.trim() || null,
      });
      setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
      setView('items');
    } catch (err) {
      setMarkError(err instanceof Error ? err.message : `Failed to save ${detailTarget === 'OK' ? 'item' : 'deficiency'}`);
    } finally {
      setIsSavingMark(false);
    }
  };

  /**
   * Holistic capture (BR-6): an arbitrary, un-enumerated set of photos for an item,
   * uploaded as a single kind='GENERAL' batch instead of the fixed WIDE/CLOSEUP pair used
   * for deficiency evidence. Uploads sequentially so `sessionPhotos`/`photoCount` stay
   * consistent even if one file in the batch fails partway through.
   */
  const handleCaptureGeneral = async (files: FileList | null) => {
    if (!files || files.length === 0 || !selectedItem) return;
    setUploadingKind('GENERAL');
    setUploadError(null);
    try {
      for (const file of Array.from(files)) {
        const photo = await uploadConditionPhoto(selectedItem.id, file, 'GENERAL');
        setSessionPhotos((prev) => ({
          ...prev,
          [selectedItem.id]: [...(prev[selectedItem.id] ?? []), photo],
        }));
        setItems((prev) =>
          prev.map((i) => (i.id === selectedItem.id ? { ...i, photoCount: i.photoCount + 1 } : i))
        );
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Failed to upload photo');
    } finally {
      setUploadingKind(null);
    }
  };

  /**
   * Remove a photo from the current item (BR-24, DRAFT-only). Optimistic: drops it from
   * both `sessionPhotos` (this-session uploads) and `selectedItem.photos` (server-loaded
   * photos from a resumed/re-opened item) and decrements `photoCount` immediately. On
   * failure, re-fetches the whole walkthrough (no single-item GET exists) so the UI
   * reflects the true server state rather than a stale optimistic removal.
   *
   * Split into a request/confirm pair (FM6/FM7) so the destructive confirmation goes
   * through the app's own themed `Modal` instead of `window.confirm`.
   */
  const requestRemovePhoto = (photo: { id: string }) => setPhotoPendingRemoval({ id: photo.id });

  const confirmRemovePhoto = async () => {
    if (!selectedItem || !photoPendingRemoval) return;
    const photo = photoPendingRemoval;
    setPhotoPendingRemoval(null);
    setRemovingPhotoId(photo.id);
    setUploadError(null);
    try {
      await deleteConditionPhoto(photo.id);
      setSessionPhotos((prev) => ({
        ...prev,
        [selectedItem.id]: (prev[selectedItem.id] ?? []).filter((p) => p.id !== photo.id),
      }));
      setItems((prev) =>
        prev.map((i) =>
          i.id === selectedItem.id
            ? { ...i, photoCount: Math.max(0, i.photoCount - 1), photos: i.photos.filter((p) => p.id !== photo.id) }
            : i
        )
      );
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Failed to remove photo');
      await loadWalkthrough();
    } finally {
      setRemovingPhotoId(null);
    }
  };

  const handleSubmitCorrection = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!correctionItemId || !correctionNote.trim()) return;
    setIsSavingCorrection(true);
    setCorrectionError(null);
    try {
      await addConditionCorrection(correctionItemId, { note: correctionNote.trim() });
      setCorrectionItemId(null);
      setCorrectionNote('');
    } catch (err) {
      setCorrectionError(err instanceof Error ? err.message : 'Failed to add correction note');
    } finally {
      setIsSavingCorrection(false);
    }
  };

  const handleSubmitWalkthrough = async () => {
    if (!summary) return;
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitGaps(null);
    setConfirmPhotoOverride(false);
    try {
      const { walkthrough } = await submitWalkthrough(summary.id, { overridePhotoRequirement: confirmPhotoOverride });
      setSummary(walkthrough);
      setView('rooms');
    } catch (err) {
      if (err instanceof WalkthroughIncompleteClientError) {
        setSubmitGaps(err.gaps);
      } else {
        setSubmitError(err instanceof Error ? err.message : 'Failed to submit walkthrough');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const jumpToGap = (gap: SubmitGap) => {
    const item = items.find((i) => i.id === gap.itemId);
    if (!item) return;
    setSelectedRoom(item.roomName);
    if (gap.reason === 'NOT_ASSESSED') {
      // Not yet assessed at all — land on the item list so the user chooses OK/Flag.
      setView('items');
      return;
    }
    // MISSING_PHOTO or MISSING_DEFICIENCY_TYPE — open detail with the item's own
    // condition as the target so the fix-up (add photo / add type) matches what's stored.
    openItemDetail(item, item.condition === 'DEFICIENT' ? 'DEFICIENT' : 'OK');
  };

  if (!leaseId || !phase) {
    return (
      <div className="px-[var(--page-padding-x)] py-8" data-testid="walkthrough-runner-page">
        <p className="text-sm text-destructive" role="alert">Missing lease or phase.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto pb-6" data-testid="walkthrough-runner-page">
        <div className="px-[var(--page-padding-x)] pt-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-24 mt-2" />
        </div>
        <div className="px-[var(--page-padding-x)] mt-4 space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      </div>
    );
  }

  const phaseLabel = phase === 'MOVE_IN' ? 'Move-In' : 'Move-Out';

  return (
    <div className="max-w-2xl mx-auto pb-24" data-testid="walkthrough-runner-page">
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title={`${phaseLabel} Walkthrough`}
          subtitleNode={
            <span data-testid="walkthrough-progress">
              {assessedCount} of {items.length} assessed
              {deficientCount > 0 && ` · ${deficientCount} flagged`}
              {isFrozen && ` · ${summary?.status}`}
            </span>
          }
          backLink={{ label: 'Lease', to: `/leases/${leaseId}` }}
        />
      </div>

      {/* Progress bar */}
      <div className="px-[var(--page-padding-x)] mb-4">
        <div className="h-2 w-full rounded-full bg-muted overflow-hidden" role="progressbar"
          aria-valuenow={assessedCount} aria-valuemin={0} aria-valuemax={items.length || 1}
          aria-label="Walkthrough completeness">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${items.length ? (assessedCount / items.length) * 100 : 0}%` }}
          />
        </div>
      </div>

      {error && (
        <div className="mx-[var(--page-padding-x)] mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm" role="alert" data-testid="error-message">
          <p className="text-destructive">{error}</p>
        </div>
      )}

      {/* Back control for nested views (since backLink can't run client logic) */}
      {view !== 'rooms' && (
        <div className="px-[var(--page-padding-x)] mb-2">
          <button
            type="button"
            data-testid="runner-back-button"
            onClick={() => setView(view === 'detail' ? 'items' : 'rooms')}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground min-h-[44px]"
          >
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
        </div>
      )}

      {/* ── ROOMS VIEW ───────────────────────────────────────────────── */}
      {view === 'rooms' && (
        <div className="px-[var(--page-padding-x)] flex flex-col gap-2" data-testid="room-list">
          {roomGroups.length === 0 && (
            <div
              className="rounded-lg border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground text-center flex flex-col items-center gap-2"
              data-testid="no-template-notice"
            >
              <span>This unit doesn't have a condition template yet. Set one up before starting a walkthrough.</span>
              {(summary?.unitId ?? templateMissingUnitId) && (
                <Link
                  to={`/units/${summary?.unitId ?? templateMissingUnitId}/condition-template`}
                  className="inline-flex items-center justify-center min-h-[44px] px-3 text-sm font-medium text-primary hover:underline"
                  data-testid="no-template-notice-link"
                >
                  Set up template
                </Link>
              )}
            </div>
          )}
          {roomGroups.map((group) => {
            const roomAssessed = group.items.filter((i) => i.condition !== 'NOT_ASSESSED').length;
            const roomDeficient = group.items.filter((i) => i.condition === 'DEFICIENT').length;
            return (
              <button
                key={group.roomName}
                type="button"
                data-testid={`room-tile-${group.roomName}`}
                onClick={() => { setSelectedRoom(group.roomName); setView('items'); }}
                className="flex items-center justify-between gap-2 px-4 py-4 min-h-[56px] rounded-lg border border-border bg-card hover:bg-accent text-left"
              >
                <div>
                  <p className="text-sm font-medium text-foreground">{group.roomName}</p>
                  <p className="text-xs text-muted-foreground">
                    {roomAssessed} of {group.items.length} assessed
                    {roomDeficient > 0 && ` · ${roomDeficient} flagged`}
                  </p>
                </div>
                <ChevronLeft className="h-4 w-4 rotate-180 text-muted-foreground shrink-0" />
              </button>
            );
          })}

          <div className="mt-4 flex flex-col gap-2">
            {submitError && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm" role="alert">
                <p className="text-destructive">{submitError}</p>
              </div>
            )}
            {submitGaps && submitGaps.length > 0 && (
              <div className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm" role="alert" data-testid="submit-gaps">
                <p className="font-medium mb-2 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Needs attention before you can submit:</p>
                <ul className="flex flex-col gap-1">
                  {submitGaps.map((gap) => (
                    <li key={gap.itemId}>
                      <button
                        type="button"
                        data-testid={`submit-gap-${gap.itemId}`}
                        onClick={() => jumpToGap(gap)}
                        className="text-sm underline text-left"
                      >
                        {gap.itemName} — {gap.reason.replace(/_/g, ' ').toLowerCase()}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!isFrozen ? (
              <Button
                onClick={() => { setSubmitGaps(null); setView('review'); }}
                disabled={items.length === 0}
                data-testid="review-submit-button"
              >
                Review &amp; Submit
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground text-center" data-testid="walkthrough-frozen-notice">
                This walkthrough has been {summary?.status.toLowerCase()} and can no longer be edited.
                Use a correction note on any item below to add information.
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── ITEMS VIEW ───────────────────────────────────────────────── */}
      {view === 'items' && selectedRoom && (
        <div className="px-[var(--page-padding-x)] flex flex-col gap-2" data-testid="item-list">
          <h2 className="text-sm font-semibold text-foreground">{selectedRoom}</h2>
          {roomGroups
            .find((g) => g.roomName === selectedRoom)
            ?.items.map((item) => (
              <div key={item.id} className="rounded-lg border border-border bg-card p-3" data-testid={`item-tile-${item.id}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{item.itemName}</p>
                    <p className="text-xs text-muted-foreground">{ITEM_CATEGORY_LABELS[item.category]}</p>
                  </div>
                  <ConditionBadge condition={item.condition} />
                </div>

                {/* Inline detail (BR-24): photo count, narrative presence, flagged deficiency
                    types — surfaces enough of what's on record that "Edit" isn't a blind door. */}
                {item.condition !== 'NOT_ASSESSED' && (
                  <div
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2 text-xs text-muted-foreground"
                    data-testid={`item-inline-detail-${item.id}`}
                  >
                    {item.photoCount > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <ImageIcon className="h-3 w-3" /> {item.photoCount} photo{item.photoCount === 1 ? '' : 's'}
                      </span>
                    )}
                    {item.narrative && (
                      <span className="inline-flex items-center gap-1">
                        <FileText className="h-3 w-3" /> Note added
                      </span>
                    )}
                    {item.condition === 'DEFICIENT' && item.deficiencyTypes.length > 0 && (
                      <span className="truncate">
                        {item.deficiencyTypes.map((t) => DEFICIENCY_TYPE_LABELS[t]).join(', ')}
                      </span>
                    )}
                  </div>
                )}

                {!isFrozen ? (
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      data-testid={`mark-ok-${item.id}`}
                      onClick={() => markOk(item)}
                      className={`flex items-center justify-center gap-1.5 min-h-[48px] rounded-md text-sm font-semibold border ${
                        item.condition === 'OK'
                          ? 'bg-success text-success-foreground border-success/25'
                          : 'border-border hover:bg-accent text-foreground'
                      }`}
                    >
                      <Check className="h-4 w-4" /> OK
                    </button>
                    <button
                      type="button"
                      data-testid={`flag-item-${item.id}`}
                      onClick={() => openItemDetail(item, 'DEFICIENT')}
                      className={`flex items-center justify-center gap-1.5 min-h-[48px] rounded-md text-sm font-semibold border ${
                        item.condition === 'DEFICIENT'
                          ? 'bg-destructive text-destructive-foreground border-destructive'
                          : 'border-border hover:bg-accent text-foreground'
                      }`}
                    >
                      <Flag className="h-4 w-4" /> Flag
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid={`add-correction-${item.id}`}
                    onClick={() => { setCorrectionItemId(item.id); setCorrectionNote(''); setCorrectionError(null); }}
                    className="flex items-center gap-1.5 min-h-[44px] text-sm text-primary"
                  >
                    <MessageSquarePlus className="h-4 w-4" /> Add correction note
                  </button>
                )}

                <div className="mt-2 flex items-center gap-4 flex-wrap">
                  {/* Persistent Edit affordance (BR-24/US-18): re-open any assessed item to
                      flip OK<->Flag, edit the narrative, or add/remove photos. */}
                  {!isFrozen && item.condition !== 'NOT_ASSESSED' && (
                    <button
                      type="button"
                      data-testid={`edit-item-${item.id}`}
                      onClick={() => openItemDetail(item, item.condition === 'DEFICIENT' ? 'DEFICIENT' : 'OK')}
                      className="inline-flex items-center gap-1.5 min-h-[44px] text-xs font-medium text-primary underline"
                    >
                      <Pencil className="h-3 w-3" /> Edit
                    </button>
                  )}

                  {/* Tile-level photo nudge (BR-6): non-blocking, one-tap shortcut into
                      detail for a photoless OK item — surfaces the "add a photo" suggestion
                      directly on the tile instead of hiding it behind Edit. Disappears once
                      a photo is attached (photoCount > 0) or the item stops being OK.
                      HOLISTIC-mode only (matches the pre-iteration-2 behavior): in
                      DEFICIENCY mode the capture flow is defect-only, so an OK item is never
                      nudged for a photo. */}
                  {!isFrozen && summary?.mode === 'HOLISTIC' && item.condition === 'OK' && item.photoCount === 0 && (
                    <button
                      type="button"
                      data-testid={`photo-nudge-${item.id}`}
                      onClick={() => openItemDetail(item, 'OK')}
                      className="inline-flex items-center gap-1.5 min-h-[44px] text-xs font-medium text-warning hover:text-warning"
                    >
                      <Camera className="h-3 w-3" /> Add a photo (optional)
                    </button>
                  )}
                </div>
              </div>
            ))}
          {(() => {
            const currentIndex = roomGroups.findIndex((g) => g.roomName === selectedRoom);
            const nextRoom = currentIndex >= 0 ? roomGroups[currentIndex + 1] : undefined;
            return (
              <Button
                onClick={() => {
                  if (nextRoom) {
                    setSelectedRoom(nextRoom.roomName);
                  } else {
                    setSubmitGaps(null);
                    setView('review');
                  }
                }}
                data-testid="next-room-button"
              >
                {nextRoom ? `Next Room: ${nextRoom.roomName}` : 'Done'}
              </Button>
            );
          })()}
        </div>
      )}

      {/* ── ITEM DETAIL (deficiency capture) ─────────────────────────── */}
      {view === 'detail' && selectedItem && (
        <div className="px-[var(--page-padding-x)] flex flex-col gap-4" data-testid="item-detail">
          <div>
            <h2 className="text-base font-semibold text-foreground">{selectedItem.itemName}</h2>
            <p className="text-xs text-muted-foreground">
              {selectedItem.roomName} · {ITEM_CATEGORY_LABELS[selectedItem.category]}
              {' '}· <span data-testid="detail-target">{detailTarget === 'OK' ? 'Marking OK' : 'Reporting deficiency'}</span>
            </p>
          </div>

          {markError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" role="alert">{markError}</div>
          )}

          {detailTarget === 'DEFICIENT' && (
            <fieldset>
              <legend className="text-sm font-medium text-foreground mb-2">Deficiency Type(s) *</legend>
              <div className="grid grid-cols-2 gap-2" data-testid="deficiency-type-options">
                {DEFICIENCY_TYPES.map((type) => (
                  <label key={type} className="flex items-center gap-2 text-sm rounded-md border border-border px-3 py-2.5 min-h-[44px] cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`deficiency-type-${type}`}
                      checked={deficiencyTypes.includes(type)}
                      onChange={() => toggleDeficiencyType(type)}
                      disabled={isFrozen}
                    />
                    {DEFICIENCY_TYPE_LABELS[type]}
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          <div>
            <label htmlFor="narrative-textarea" className="block mb-1 text-sm font-medium text-foreground">Narrative</label>
            <Textarea
              id="narrative-textarea"
              data-testid="narrative-textarea"
              rows={4}
              className="px-3 py-2.5 box-border bg-background"
              value={narrative}
              onChange={(e) => setNarrative(e.target.value)}
              disabled={isFrozen}
              maxLength={4000}
              placeholder={detailTarget === 'OK' ? 'Add any notes (optional)…' : 'Describe the deficiency…'}
            />
          </div>

          <div>
            <p className="block mb-1 text-sm font-medium text-foreground">
              Photos ({selectedItem.photoCount} attached)
            </p>
            {detailTarget === 'OK' && selectedItem.photoCount === 0 && (
              <p className="text-xs text-muted-foreground mb-2" data-testid="photoless-nudge">
                No photos yet — attaching photos is optional but recommended.
              </p>
            )}
            {detailTarget === 'DEFICIENT' && (
              <p className="text-xs text-muted-foreground mb-2" data-testid="wide-closeup-reminder">
                Capture a wide shot and a close-up for the best evidence.
              </p>
            )}
            {uploadError && <p className="text-sm text-destructive mb-2" role="alert">{uploadError}</p>}
            <button
              type="button"
              data-testid="capture-general-button"
              onClick={() => generalInputRef.current?.click()}
              disabled={uploadingKind !== null}
              className="flex items-center justify-center gap-1.5 min-h-[48px] w-full rounded-md border border-border hover:bg-accent text-sm font-medium disabled:opacity-60 mb-2"
            >
              <Camera className="h-4 w-4" /> {uploadingKind === 'GENERAL' ? 'Uploading…' : 'Add photos'}
            </button>
            <input
              ref={generalInputRef}
              type="file"
              accept="image/*"
              multiple
              className="sr-only"
              aria-label="Choose photos to add"
              onChange={(e) => handleCaptureGeneral(e.target.files)}
            />
            {displayedPhotos.length > 0 && (
              <div className="flex gap-3 flex-wrap" data-testid="session-photo-thumbnails">
                {displayedPhotos.map((photo) => {
                  // FM5: an <img> load failure is interpreted through `status`. A PENDING photo
                  // that fails to load is still being normalized (its raw upload — e.g. an iPhone
                  // HEIC — isn't browser-renderable yet), so show a neutral "Processing…" state,
                  // NOT an error; the Centrifugo approval handler swaps it for the real thumbnail
                  // once normalization completes. Only a failed load on an already-APPROVED photo
                  // is a genuine "Unavailable" error.
                  const hasFailed = failedPhotoIds.has(photo.id);
                  const isProcessing = hasFailed && photo.status === 'PENDING';
                  const isUnavailable = hasFailed && photo.status !== 'PENDING';
                  // Cache-bust once approved-after-failure so the browser re-fetches the normalized
                  // bytes instead of the cached, un-decodable raw upload (same authz proxy URL).
                  const src = refreshedPhotoIds.has(photo.id)
                    ? `${conditionPhotoDownloadUrl(photo.id)}?v=1`
                    : conditionPhotoDownloadUrl(photo.id);
                  return (
                    <div key={photo.id} className="relative">
                      {isProcessing ? (
                        <div
                          className="h-16 w-16 rounded-md border border-dashed border-border bg-muted flex flex-col items-center justify-center gap-0.5 p-1 text-center"
                          data-testid={`photo-processing-${photo.id}`}
                          role="status"
                          aria-label="Photo processing"
                        >
                          <Loader2 className="h-4 w-4 text-muted-foreground shrink-0 animate-spin" />
                          <span className="text-[10px] leading-tight text-muted-foreground">Processing…</span>
                        </div>
                      ) : isUnavailable ? (
                        <div
                          className="h-16 w-16 rounded-md border border-dashed border-warning/50 bg-warning/10 flex flex-col items-center justify-center gap-0.5 p-1 text-center"
                          data-testid={`photo-unavailable-${photo.id}`}
                        >
                          <AlertTriangle className="h-4 w-4 text-warning shrink-0" />
                          <span className="text-[10px] leading-tight text-warning">Unavailable</span>
                        </div>
                      ) : (
                        <img
                          src={src}
                          alt={`${photo.kind === 'WIDE' ? 'Wide' : photo.kind === 'CLOSEUP' ? 'Close-up' : 'General'} photo of ${selectedItem.itemName}`}
                          className="h-16 w-16 object-cover rounded-md border border-border"
                          onError={() => setFailedPhotoIds((prev) => (prev.has(photo.id) ? prev : new Set(prev).add(photo.id)))}
                        />
                      )}
                      {!isFrozen && (
                        <button
                          type="button"
                          data-testid={`remove-photo-${photo.id}`}
                          onClick={() => requestRemovePhoto(photo)}
                          disabled={removingPhotoId === photo.id}
                          aria-label="Remove photo"
                          className="absolute -top-2 -right-2 flex items-center justify-center h-11 w-11 rounded-full disabled:opacity-60"
                        >
                          <span className="flex items-center justify-center h-5 w-5 rounded-full bg-destructive text-destructive-foreground shadow">
                            <X className="h-3 w-3" />
                          </span>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {!isFrozen && (
            <Button onClick={saveAssessment} disabled={isSavingMark} data-testid="save-deficiency-button">
              {isSavingMark ? 'Saving…' : detailTarget === 'OK' ? 'Save' : 'Save Deficiency'}
            </Button>
          )}
        </div>
      )}

      {/* ── PRE-SUBMIT REVIEW ────────────────────────────────────────── */}
      {view === 'review' && (
        <div className="px-[var(--page-padding-x)] flex flex-col gap-3" data-testid="pre-submit-review">
          <p className="text-sm text-muted-foreground">
            Review every item before submitting. Once submitted, marks are locked (corrections only).
          </p>
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              data-testid={`review-item-${item.id}`}
              disabled={isFrozen}
              onClick={() => {
                setSelectedRoom(item.roomName);
                openItemDetail(item, item.condition === 'DEFICIENT' ? 'DEFICIENT' : 'OK');
              }}
              className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2.5 w-full text-left hover:bg-accent disabled:hover:bg-card disabled:cursor-default"
            >
              <div className="min-w-0">
                <p className="text-sm text-foreground truncate">{item.roomName} · {item.itemName}</p>
                {item.condition === 'DEFICIENT' && item.deficiencyTypes.length > 0 && (
                  <p className="text-xs text-muted-foreground truncate">
                    {item.deficiencyTypes.map((t) => DEFICIENCY_TYPE_LABELS[t]).join(', ')}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <ConditionBadge condition={item.condition} />
                {/* FM7: explicit tap-to-edit affordance — mirrors the item list's Pencil +
                    "Edit" cue so this row's interactivity is discoverable by scanning, not
                    just by accident/hover. */}
                {!isFrozen && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
                    <Pencil className="h-3 w-3" /> Edit
                  </span>
                )}
              </div>
            </button>
          ))}
          {submitError && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm" role="alert">
              <p className="text-destructive">{submitError}</p>
            </div>
          )}
          {submitGaps && submitGaps.length > 0 && (
            <div className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm" role="alert" data-testid="submit-gaps">
              <p className="font-medium mb-2 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Needs attention before you can submit:</p>
              <ul className="flex flex-col gap-1">
                {submitGaps.map((gap) => (
                  <li key={gap.itemId}>
                    <button
                      type="button"
                      data-testid={`submit-gap-${gap.itemId}`}
                      onClick={() => jumpToGap(gap)}
                      className="text-sm underline text-left"
                    >
                      {gap.itemName} — {gap.reason.replace(/_/g, ' ').toLowerCase()}
                    </button>
                  </li>
                ))}
              </ul>
              {submitGaps.every((gap) => gap.reason === 'MISSING_PHOTO') && (
                <label className="mt-2 flex items-center gap-2 min-h-[44px] cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="photo-override-checkbox"
                    checked={confirmPhotoOverride}
                    onChange={(e) => setConfirmPhotoOverride(e.target.checked)}
                  />
                  Submit without photos for the item(s) above
                </label>
              )}
            </div>
          )}
          <Button onClick={handleSubmitWalkthrough} disabled={isSubmitting} data-testid="confirm-submit-button">
            {isSubmitting ? 'Submitting…' : 'Submit Walkthrough'}
          </Button>
        </div>
      )}

      {/* Correction note modal (JR-6) */}
      <Modal
        isOpen={correctionItemId !== null}
        onClose={() => setCorrectionItemId(null)}
        title="Add Correction Note"
        data-testid="correction-modal"
      >
        {correctionError && (
          <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" role="alert">{correctionError}</div>
        )}
        <form onSubmit={handleSubmitCorrection}>
          <label htmlFor="correction-note-input" className="sr-only">Correction note</label>
          <Textarea
            id="correction-note-input"
            data-testid="correction-note-input"
            rows={4}
            required
            aria-required="true"
            className="px-3 py-2.5 box-border bg-background mb-4"
            value={correctionNote}
            onChange={(e) => setCorrectionNote(e.target.value)}
            placeholder="Describe the correction…"
          />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => setCorrectionItemId(null)} disabled={isSavingCorrection}>Cancel</Button>
            <Button type="submit" disabled={isSavingCorrection || !correctionNote.trim()} data-testid="correction-submit-button">
              {isSavingCorrection ? 'Saving…' : 'Save Note'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Remove-photo confirmation (FM6/FM7): themed Modal instead of window.confirm, so
          it respects dark mode and the rest of the app's visual language. */}
      <Modal
        isOpen={photoPendingRemoval !== null}
        onClose={() => setPhotoPendingRemoval(null)}
        title="Remove Photo"
        data-testid="remove-photo-modal"
      >
        <p className="text-sm text-muted-foreground mb-4">This photo will be permanently removed. This cannot be undone.</p>
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => setPhotoPendingRemoval(null)}>Cancel</Button>
          <Button type="button" variant="destructive" onClick={confirmRemovePhoto} data-testid="confirm-remove-photo-button">Remove Photo</Button>
        </div>
      </Modal>
    </div>
  );
}

function ConditionBadge({ condition }: { condition: ConditionItemResponse['condition'] }) {
  if (condition === 'OK') return <Badge variant="outline" className="text-success border-success/40 bg-success/10">OK</Badge>;
  if (condition === 'DEFICIENT') return <Badge variant="destructive">Flagged</Badge>;
  return <Badge variant="outline" className="text-muted-foreground">Not assessed</Badge>;
}

export default WalkthroughRunnerPage;
